// AJAX navigation between collections triggered by [data-ajax-collection-link] elements.
// Swaps the entire <main class="main-content"> with the destination collection's content,
// updates <title> and URL, and supports browser back/forward via popstate.

(function () {
  const MAIN_SELECTOR = 'main.main-content, main#MainContent';
  const LINK_SELECTOR = '[data-ajax-collection-link]';
  const LOADING_CLASS = 'is-ajax-loading';

  function isModifiedClick(event) {
    return (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    );
  }

  // Mirrors theme.dev.js:858-885 ariaToggle() — re-attaches click handlers on
  // [data-aria-toggle] elements after AJAX swap. Guarded so it's safe to call
  // multiple times on the same container without double-binding.
  function reinitAriaToggles(container) {
    if (!container) return;
    const toggleButtons = container.querySelectorAll('[data-aria-toggle]');
    toggleButtons.forEach(function (element) {
      if (element.dataset.ajaxAriaInitialized) return;
      element.dataset.ajaxAriaInitialized = 'true';
      element.addEventListener('click', function (event) {
        event.preventDefault();
        const currentTarget = event.currentTarget;
        const expanded = currentTarget.getAttribute('aria-expanded') === 'true';
        currentTarget.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        const toggleID = currentTarget.getAttribute('aria-controls');
        const toggleElement = toggleID && document.getElementById(toggleID);
        if (!toggleElement) return;
        const addExpandingClass = function () {
          toggleElement.classList.add('expanding');
          toggleElement.removeEventListener('transitionstart', addExpandingClass);
        };
        const removeExpandingClass = function () {
          toggleElement.classList.remove('expanding');
          toggleElement.removeEventListener('transitionend', removeExpandingClass);
        };
        toggleElement.addEventListener('transitionstart', addExpandingClass);
        toggleElement.addEventListener('transitionend', removeExpandingClass);
        toggleElement.classList.toggle('expanded');
      });
    });
  }

  async function swapToUrl(url, options) {
    options = options || {};
    const main = document.querySelector(MAIN_SELECTOR);
    if (!main) {
      window.location.href = url;
      return;
    }

    document.body.classList.add(LOADING_CLASS);

    try {
      const response = await fetch(url, { headers: { 'X-Requested-With': 'fetch' } });
      if (!response.ok) throw new Error('Bad response: ' + response.status);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newMain = doc.querySelector(MAIN_SELECTOR);
      if (!newMain) throw new Error('No <main> in response');

      main.replaceWith(newMain);

      const newTitle = doc.querySelector('title');
      if (newTitle) document.title = newTitle.textContent;

      if (!options.skipPushState) {
        history.pushState({ ajaxCollection: true }, '', url);
      }

      // Re-attach [data-aria-toggle] click handlers on swapped content (filter button, etc.)
      // Theme.js attaches these once at init; they're lost when DOM is replaced.
      // Custom elements (popout-select, collection-component) re-init themselves via connectedCallback.
      reinitAriaToggles(document.querySelector(MAIN_SELECTOR));

      // Notify other scripts that DOM was swapped (in case any need to re-init)
      document.dispatchEvent(new CustomEvent('ajax-collection:loaded', { detail: { url: url } }));

      // Smooth-scroll to the new pills (or main) so the user sees the change
      const newPills = newMain.querySelector('.highlights');
      const scrollTarget = newPills || newMain;
      if (scrollTarget) {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (err) {
      // Fallback: full reload so user is never stranded
      console.error('AJAX collection nav failed, falling back to reload:', err);
      window.location.href = url;
      return;
    } finally {
      document.body.classList.remove(LOADING_CLASS);
    }
  }

  // Event delegation — survives DOM swaps since we listen on document
  document.addEventListener('click', function (event) {
    const link = event.target.closest(LINK_SELECTOR);
    if (!link) return;
    if (isModifiedClick(event)) return;
    if (!link.href) return;

    // Skip if the link points to the current URL (no-op)
    if (link.href === window.location.href) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    swapToUrl(link.href);
  });

  // Browser back/forward
  window.addEventListener('popstate', function (event) {
    // Only handle if the entry was set by us OR is a collection URL
    if (event.state && event.state.ajaxCollection) {
      swapToUrl(window.location.href, { skipPushState: true });
    }
  });

  // === Filter sidebar expansion: mirror .expanded onto preceding .collection__nav ===
  // (Avoids :has() so this works in all browsers, not just modern ones.)
  function syncFilterNavState(slider) {
    if (!slider) return;
    const expanded = slider.classList.contains('expanded');
    // The slider is inside .collection__products, not a sibling of .collection__nav.
    // Walk up to the common ancestor (collection-component) then query down for the nav.
    const ancestor = slider.closest(
      'collection-component, [data-section-type="collection"], [data-section-type="search"]'
    );
    const nav = ancestor ? ancestor.querySelector('.collection__nav') : null;
    if (nav) nav.classList.toggle('has-filter-open', expanded);
  }

  function observeFilterSlider(slider) {
    if (!slider || slider.dataset.filterObserverAttached) return;
    slider.dataset.filterObserverAttached = 'true';
    syncFilterNavState(slider);
    new MutationObserver(function () {
      syncFilterNavState(slider);
    }).observe(slider, { attributes: true, attributeFilter: ['class'] });
  }

  function attachToAllSliders() {
    document.querySelectorAll('.collection__sidebar__slider').forEach(observeFilterSlider);
  }

  if (document.readyState !== 'loading') {
    attachToAllSliders();
  } else {
    document.addEventListener('DOMContentLoaded', attachToAllSliders);
  }

  // Re-attach after AJAX swap (slider element is replaced wholesale)
  document.addEventListener('ajax-collection:loaded', attachToAllSliders);
})();
